import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import backIcon from "../assets/ecommerce/atras.png";
import cartIcon from "../assets/ecommerce/carrito.webp";
import { useCart } from "../context/CartContext";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "http://localhost:89"
).replace(/\/$/, "");

const formatCurrency = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const formatWholeNumber = new Intl.NumberFormat("es-MX", {
  maximumFractionDigits: 0,
});

const buildApiUrl = (path) => `${API_BASE_URL}${path}`;
const STORAGE_KEY = "omova_fibra_carbono_selection";
const normalizeString = (value) => {
  if (value === null || value === undefined) return "";
  const normalized = String(value).trim();
  return normalized.length ? normalized : "";
};
const normalizeFiberCode = (value) => normalizeString(value).toUpperCase();
const normalizeDimension = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const EcommerceFibraCarbono = () => {
  const { addItem, openCart, shippingConfig } = useCart();
  const [searchParams] = useSearchParams();
  const [fibers, setFibers] = useState([]);
  const [selectedFiber, setSelectedFiber] = useState(null);
  const [loadingFibers, setLoadingFibers] = useState(true);
  const [fibersError, setFibersError] = useState("");
  const [widthCm, setWidthCm] = useState("0");
  const [heightCm, setHeightCm] = useState("0");
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState("");
  const [pricingLoading, setPricingLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("fiber");
  const [fiberImageSrc, setFiberImageSrc] = useState(null);
  const requestedFiberCode = useMemo(
    () => normalizeFiberCode(searchParams.get("code")),
    [searchParams]
  );
  const requestedWidth = useMemo(
    () => normalizeDimension(searchParams.get("width_cm") || searchParams.get("width")),
    [searchParams]
  );
  const requestedHeight = useMemo(
    () => normalizeDimension(searchParams.get("height_cm") || searchParams.get("height")),
    [searchParams]
  );

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      if (stored.width_cm !== undefined) {
        setWidthCm(String(stored.width_cm));
      }
      if (stored.height_cm !== undefined) {
        setHeightCm(String(stored.height_cm));
      }
    } catch (error) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    let active = true;
    setLoadingFibers(true);

    fetch(buildApiUrl("/api/ecommerce/fibra-carbono/products"))
      .then(async (response) => {
        const contentType = response.headers.get("content-type") || "";
        const text = await response.text();
        if (!response.ok || !contentType.includes("application/json")) {
          throw new Error("No se pudieron cargar las fibras.");
        }
        try {
          return JSON.parse(text);
        } catch (error) {
          throw new Error("No se pudieron cargar las fibras.");
        }
      })
      .then((data) => {
        if (!active) return;
        setFibers(data);
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
        const storedFiber = data.find(
          (fiber) => fiber.code === stored.fiber_code,
        );
        if (storedFiber) {
          setSelectedFiber(storedFiber);
        } else {
          setSelectedFiber(data[0] || null);
          setWidthCm("0");
          setHeightCm("0");
          if (stored.fiber_code) {
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      })
      .catch((error) => {
        if (!active) return;
        setFibersError(error.message);
      })
      .finally(() => {
        if (!active) return;
        setLoadingFibers(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (
      loadingFibers ||
      !fibers.length ||
      !requestedFiberCode ||
      !requestedWidth ||
      !requestedHeight
    ) {
      return;
    }

    const matchedFiber = fibers.find(
      (fiber) => normalizeFiberCode(fiber.code) === requestedFiberCode
    );

    if (!matchedFiber) return;

    const maxAllowedHeight = Number(matchedFiber.max_height_cm) || 150;
    const requestedArea = requestedWidth * requestedHeight;
    const isValidConfiguration =
      requestedArea >= 600 &&
      requestedArea <= 30000 &&
      requestedHeight <= maxAllowedHeight;

    if (!isValidConfiguration) return;

    setSelectedFiber((current) => (current?.code === matchedFiber.code ? current : matchedFiber));
    setWidthCm(String(requestedWidth));
    setHeightCm(String(requestedHeight));
    setActiveTab("summary");
  }, [
    fibers,
    loadingFibers,
    requestedFiberCode,
    requestedHeight,
    requestedWidth,
  ]);

  const widthValue = Number(widthCm);
  const heightValue = Number(heightCm);
  const safeWidth = Number.isFinite(widthValue) ? Math.max(0, widthValue) : 0;
  const safeHeight = Number.isFinite(heightValue)
    ? Math.max(0, heightValue)
    : 0;
  const hasDimensions = safeWidth > 0 && safeHeight > 0;
  const cm2 = hasDimensions ? safeWidth * safeHeight : 0;
  const formattedCm2 = formatWholeNumber.format(cm2);
  const areaExceeded = cm2 > 30000;
  const areaTooSmall = cm2 > 0 && cm2 < 600;
  const maxHeight = selectedFiber?.max_height_cm ?? 150;
  const heightTooTall = safeHeight > 0 && safeHeight > maxHeight;
  const canProceed =
    hasDimensions &&
    !areaExceeded &&
    !areaTooSmall &&
    !heightTooTall &&
    !!selectedFiber;

  const handleAddToCart = () => {
    if (!selectedFiber || !hasDimensions || !pricing) return;
    addItem({
      type: "fibra-carbono",
      name: `${selectedFiber.type} (${displayWidth} X ${displayHeight} CM)`,
      unitPrice: pricing.final_cost || 0,
      payload: {
        fiber_code: selectedFiber.code,
        width_cm: widthValue,
        height_cm: heightValue,
      },
    });
    localStorage.removeItem(STORAGE_KEY);
    openCart();
  };

  useEffect(() => {
    setPricing(null);
    setPricingError("");

    if (!selectedFiber || !hasDimensions) {
      return;
    }

    if (areaTooSmall) {
      setPricingError("El área mínima es 600 cm2.");
      return;
    }

    if (areaExceeded) {
      setPricingError("El área máxima es 3 m2.");
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      setPricingLoading(true);

      fetch(buildApiUrl("/api/ecommerce/fibra-carbono/pricing"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          width_cm: widthValue,
          height_cm: heightValue,
          fiber_code: selectedFiber.code,
        }),
        signal: controller.signal,
      })
        .then(async (response) => {
          const contentType = response.headers.get("content-type") || "";
          const text = await response.text();
          if (!response.ok || !contentType.includes("application/json")) {
            throw new Error("No se pudo calcular el precio.");
          }
          try {
            return JSON.parse(text);
          } catch (error) {
            throw new Error("No se pudo calcular el precio.");
          }
        })
        .then((data) => {
          setPricing(data);
        })
        .catch((error) => {
          if (error.name === "AbortError") return;
          setPricingError(error.message);
        })
        .finally(() => {
          setPricingLoading(false);
        });
    }, 350);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [
    selectedFiber,
    widthValue,
    heightValue,
    hasDimensions,
    areaExceeded,
    areaTooSmall,
  ]);

  const displayWidth = safeWidth;
  const displayHeight = safeHeight;

  const summary = useMemo(() => {
    if (!pricing) return "--";
    return formatCurrency.format(pricing.final_cost || 0);
  }, [pricing]);

  const shippingLabel = useMemo(() => {
    if (!pricing) return "--";
    const finalCost = Number(pricing.final_cost);
    if (!Number.isFinite(finalCost)) return "--";
    const threshold = shippingConfig?.threshold ?? 499;
    const cost = shippingConfig?.cost ?? 199;
    return finalCost >= threshold ? "Gratis" : formatCurrency.format(cost);
  }, [pricing, shippingConfig]);
  const technicalInformation = normalizeString(selectedFiber?.technical_information);
  const presentationsText = normalizeString(selectedFiber?.presentations);
  const processCompatibility = normalizeString(selectedFiber?.compatibility);
  const hasCommercialDetails =
    technicalInformation || presentationsText || processCompatibility;

  useEffect(() => {
    if (!selectedFiber) return;
    const payload = {
      fiber_code: selectedFiber.code,
      width_cm: widthCm,
      height_cm: heightCm,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [selectedFiber, widthCm, heightCm]);

  useEffect(() => {
    if (loadingFibers || !selectedFiber) {
      setFiberImageSrc(null);
      return;
    }

    const src = selectedFiber?.image_url;
    if (!src) {
      setFiberImageSrc("/ecommerce/Fibras.png");
      return;
    }

    let active = true;
    setFiberImageSrc(null);

    const img = new Image();
    img.onload = () => {
      if (!active) return;
      setFiberImageSrc(src);
    };
    img.onerror = () => {
      if (!active) return;
      setFiberImageSrc("/ecommerce/Fibras.png");
    };
    img.src = src;

    return () => {
      active = false;
    };
  }, [loadingFibers, selectedFiber]);

  return (
    <div className="relative px-8 md:px-14 py-6 md:py-8 overflow-hidden h-auto md:h-full flex flex-col bg-white">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col flex-1">
        <div className="grid grid-cols-2 grid-rows-[auto,auto] items-center gap-3 md:gap-4 mb-5 md:mb-6 md:grid-cols-[160px,1fr,160px] md:grid-rows-1">
          <Link
            to="/ecommerce"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-800 justify-self-start"
          >
            <img
              src={backIcon}
              alt="Atrás"
              className="w-8 h-8 md:w-10 md:h-10"
            />
            <span className="text-sm md:text-base font-semibold">Atrás</span>
          </Link>
          <button
            type="button"
            onClick={openCart}
            className="justify-self-end cursor-pointer md:col-start-3"
          >
            <img
              src={cartIcon}
              alt="Carrito"
              className="w-14 h-14 md:w-16 md:h-16"
            />
          </button>
          <h1 className="col-span-2 row-start-2 text-xl md:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-gray-900 text-center max-w-[420px] md:max-w-[1000px] w-full justify-self-center leading-tight md:col-span-1 md:col-start-2 md:row-start-1">
            Compra fibra y resina con las medidas que tu necesitas ¡hasta 3 m
            <sup>2</sup>!
          </h1>
        </div>

        <div className="flex flex-col md:flex-row items-start gap-2 md:gap-3 flex-1 min-h-0">
          <div className="md:w-[50%] w-full h-auto md:h-full shrink-0 mb-12 md:mb-0">
            <div className="relative h-full flex items-start justify-end md:pt-2">
              <div className="relative inline-flex items-start justify-start w-[520px] h-[320px] md:w-[620px] md:h-[500px] pb-10 md:pb-0 overflow-visible">
                {fiberImageSrc && (
                  <img
                    src={fiberImageSrc}
                    alt={selectedFiber?.type || "Fibra de carbono"}
                    className="block w-full h-full object-fill"
                  />
                )}
                <div className="absolute left-0 top-0 bottom-0 -translate-x-8 md:-translate-x-10 flex items-center">
                  <div className="relative h-full w-10 md:w-12">
                    <div className="absolute left-1/2 -translate-x-1/2 top-3 bottom-3 w-[2px] bg-gray-900"></div>
                    <div className="absolute left-1/2 -translate-x-1/2 top-3 w-4 h-[2px] bg-gray-900"></div>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-3 w-4 h-[2px] bg-gray-900"></div>
                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-90 text-sm md:text-base font-semibold text-gray-900 whitespace-nowrap bg-white px-1 text-center tabular-nums">
                      {displayHeight} cm
                    </span>
                  </div>
                </div>
                <div className="absolute left-0 right-0 bottom-0 translate-y-0 md:translate-y-10">
                  <div className="relative w-full h-10 md:h-12">
                    <div className="absolute left-3 right-3 top-1/2 -translate-y-1/2 h-[2px] bg-gray-900"></div>
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-[2px] h-4 bg-gray-900"></div>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 w-[2px] h-4 bg-gray-900"></div>
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-0 text-base md:text-lg font-semibold text-gray-900 whitespace-nowrap tabular-nums">
                      {displayWidth} cm
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:w-[50%] w-full md:flex-1 min-h-0 md:h-full">
            <div className="h-full min-h-0 flex flex-col">
              <div className="flex items-center justify-between bg-black text-white px-4 py-2 rounded-lg">
                <button
                  type="button"
                  className="text-white/70"
                  aria-label="Paso anterior"
                  onClick={() => setActiveTab("fiber")}
                >
                  <img
                    src={backIcon}
                    alt="Atrás"
                    className="w-5 h-5 md:w-6 md:h-6"
                  />
                </button>
                <div className="flex items-center justify-evenly flex-1 mx-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab("fiber")}
                    className={`px-4 py-1 text-xs font-bold rounded-sm shadow-sm ${
                      activeTab === "fiber"
                        ? "bg-white text-black"
                        : "text-white/50"
                    }`}
                  >
                    FIBRA
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!canProceed) return;
                      setActiveTab("summary");
                    }}
                    disabled={!canProceed}
                    className={`px-4 py-1 text-xs font-bold rounded-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                      activeTab === "summary"
                        ? "bg-white text-black"
                        : "text-white/50"
                    }`}
                  >
                    RESUMEN
                  </button>
                </div>
                <button
                  type="button"
                  className="text-white/70"
                  aria-label="Paso siguiente"
                  onClick={() => {
                    if (!canProceed) return;
                    setActiveTab("summary");
                  }}
                  disabled={!canProceed}
                >
                  <img
                    src={backIcon}
                    alt="Siguiente"
                    className="w-5 h-5 md:w-6 md:h-6 rotate-180"
                  />
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-5 py-4 pb-10 md:pb-12 space-y-4">
                {activeTab === "fiber" ? (
                  <>
                    <div className="rounded-xl border border-black/30 bg-white/80 px-3 py-3 space-y-3">
                      <h2 className="text-sm md:text-base font-semibold text-gray-900 text-left">
                        Selecciona el tipo de fibra Omova:
                      </h2>
                      {loadingFibers ? (
                        <div className="text-sm text-gray-500">
                          Cargando fibras...
                        </div>
                      ) : fibersError ? (
                        <div className="text-xs text-red-600 font-semibold">
                          {fibersError}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {fibers.map((fiber) => {
                            const isSelected =
                              selectedFiber?.code === fiber.code;
                            return (
                              <button
                                key={fiber.code}
                                type="button"
                                onClick={() => setSelectedFiber(fiber)}
                                className={`px-3 py-1.5 text-[11px] md:text-xs font-bold uppercase tracking-wide rounded-full border transition ${
                                  isSelected
                                    ? "bg-white border-black text-primary-green shadow"
                                    : "bg-black border-black text-white hover:bg-gray-900"
                                }`}
                              >
                                {fiber.type}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="rounded-xl border border-black/30 bg-white/80 px-3 py-3 space-y-3">
                      <h3 className="text-sm md:text-base font-semibold text-gray-900 text-left">
                        Medidas para tu fibra:
                      </h3>
                      <div className="flex flex-wrap items-end gap-3 text-sm md:text-base">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-xs font-semibold text-gray-700">
                            Alto
                          </span>
                          <div className="flex items-center gap-2 bg-white border-2 border-black text-primary-green px-3 py-1.5 rounded-lg">
                            <input
                              type="number"
                              min="1"
                              inputMode="numeric"
                              value={heightCm}
                              onChange={(event) =>
                                setHeightCm(event.target.value)
                              }
                              className="w-14 bg-transparent text-primary-green font-bold text-center outline-none tabular-nums"
                            />
                            <span className="text-gray-700 font-semibold">
                              cm
                            </span>
                          </div>
                        </div>
                        <span className="text-lg font-bold text-gray-700">
                          ×
                        </span>
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-xs font-semibold text-gray-700">
                            Largo
                          </span>
                          <div className="flex items-center gap-2 bg-white border-2 border-black text-primary-green px-3 py-1.5 rounded-lg">
                            <input
                              type="number"
                              min="1"
                              inputMode="numeric"
                              value={widthCm}
                              onChange={(event) =>
                                setWidthCm(event.target.value)
                              }
                              className="w-14 bg-transparent text-primary-green font-bold text-center outline-none tabular-nums"
                            />
                            <span className="text-gray-700 font-semibold">
                              cm
                            </span>
                          </div>
                        </div>
                        <span className="text-gray-500 font-semibold">
                          = {formattedCm2} cm<sup>2</sup>
                        </span>
                      </div>
                      {pricingError && !areaExceeded && !areaTooSmall && (
                        <p className="text-xs text-red-600 font-semibold">
                          {pricingError}
                        </p>
                      )}
                      {areaTooSmall && (
                        <p className="text-xs text-red-600 font-semibold">
                          El área mínima es 600 cm<sup>2</sup>.
                        </p>
                      )}
                      {heightTooTall && (
                        <p className="text-xs text-red-600 font-semibold">
                          El alto máximo de esta fibra es de: {maxHeight} cm
                        </p>
                      )}
                      {areaExceeded && (
                        <p className="text-xs text-red-600 font-semibold">
                          El área máxima es de: 30000 cm<sup>2</sup>. ¿Necesitas
                          más?{" "}
                          <Link
                            to="/form"
                            className="text-primary-green font-semibold hover:underline"
                          >
                            Regístrate
                          </Link>{" "}
                          y compra en nuestra plataforma mayorista.
                        </p>
                      )}
                    </div>

                    <div className="rounded-xl bg-dark-gray text-white px-4 py-3 space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span>Subtotal:</span>
                        <span className="font-bold">
                          {pricingLoading ? "Calculando..." : summary}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-white/70">
                        <span>Envío:</span>
                        <span>
                          {pricingLoading ? "Calculando..." : shippingLabel}
                        </span>
                      </div>
                    </div>

                    <div className="text-xs md:text-sm text-gray-700">
                      Este producto está limitado a 3 m<sup>2</sup>. ¿Necesitas
                      más?{" "}
                      <Link
                        to="/form"
                        className="text-primary-green font-semibold hover:underline"
                      >
                        Regístrate
                      </Link>{" "}
                      y compra en nuestra plataforma mayorista.
                    </div>

                    <div className="flex justify-end pb-2">
                      <button
                        type="button"
                        disabled={!canProceed}
                        onClick={() => setActiveTab("summary")}
                        className="px-6 py-2 rounded-full bg-primary-green text-white font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-6 text-left">
                    <div className="space-y-2 w-full">
                      <div className="text-2xl md:text-3xl font-extrabold uppercase text-gray-900">
                        {selectedFiber?.type || "FIBRA"}
                      </div>
                      <div className="text-base md:text-lg font-semibold text-gray-600 uppercase">
                        ({displayWidth} X {displayHeight} CM)
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                      <div className="space-y-2">
                        <div className="text-base md:text-lg font-semibold text-gray-700">
                          Envío:
                        </div>
                        <div
                          className={`text-2xl md:text-3xl font-extrabold ${
                            shippingLabel === "Gratis"
                              ? "text-primary-green"
                              : "text-gray-900"
                          }`}
                        >
                          {pricingLoading ? "Calculando..." : shippingLabel}
                        </div>

                        <div className="pt-3 space-y-1">
                          <div className="text-base md:text-lg font-semibold text-gray-700">
                            Subtotal:
                          </div>
                          <div className="text-3xl md:text-4xl font-extrabold text-gray-900">
                            {pricingLoading ? "Calculando..." : summary}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-3 md:items-end">
                        <button
                          type="button"
                          onClick={handleAddToCart}
                          className="px-8 py-2.5 rounded-full bg-pink-500 text-white font-bold shadow-md w-fit text-base md:text-lg"
                        >
                          Añadir al carrito
                        </button>
                      </div>
                    </div>

                    <div className="text-xl md:text-2xl font-extrabold text-gray-900">
                      RESUMEN
                    </div>
                    <div className="rounded-xl bg-dark-gray text-white px-5 py-4 text-base">
                      • FIBRA DE CARBONO REAL {selectedFiber?.type || ""} OMOVA
                      MEDIDA {displayWidth} X {displayHeight} CM.
                    </div>
                    {hasCommercialDetails && (
                      <div className="rounded-xl border border-black/20 bg-white px-4 py-4 text-sm md:text-base text-gray-800 space-y-2">
                        {technicalInformation && (
                          <p>
                            <span className="font-bold">Información técnica:</span>{" "}
                            {technicalInformation}
                          </p>
                        )}
                        {presentationsText && (
                          <p>
                            <span className="font-bold">Presentaciones:</span>{" "}
                            {presentationsText}
                          </p>
                        )}
                        {processCompatibility && (
                          <p>
                            <span className="font-bold">Compatibilidad de procesos:</span>{" "}
                            {processCompatibility}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EcommerceFibraCarbono;
